import { Box, Text, UnorderedList } from '@chakra-ui/react';
import { Button, Divider, Grid } from '@time-line/components/common';
import { checkIsAboveLgWidth } from '@time-line/components/utils';
import { useState } from 'react';
import { useEffectOnce, useWindowSize } from 'react-use';
import type { Department as DepartmentType } from 'types/cms/blocks';

import { Person } from './Person';

interface DepartmentProps extends DepartmentType {
  drawerHeader: {
    title?: string;
    subTitle?: string;
  };
}

const VISIBLE_INCREMENTS_MOBILE = 6;
const VISIBLE_INCREMENTS_DESKTOP = 8;

export function Department({ title, people, drawerHeader }: DepartmentProps) {
  const { width } = useWindowSize();
  const [visiblePeople, setVisiblePeople] = useState(people);
  const [showMoreAmount, setShowMoreAmount] = useState(0);

  useEffectOnce(() => {
    const isLargeScreen = checkIsAboveLgWidth(width);

    const initialVisibleAmount = isLargeScreen
      ? VISIBLE_INCREMENTS_DESKTOP
      : VISIBLE_INCREMENTS_MOBILE;

    setVisiblePeople(people.slice(0, initialVisibleAmount));
    setShowMoreAmount(
      isLargeScreen ? VISIBLE_INCREMENTS_DESKTOP : VISIBLE_INCREMENTS_MOBILE,
    );
  });

  const hasHiddenPeople = people.length > visiblePeople.length;

  const handleShowMore = () => {
    const amountToShow = visiblePeople.length + showMoreAmount;
    setVisiblePeople(people.slice(0, amountToShow));
  };

  return (
    <Grid className="team_department" gridRowGap={{ base: '4xs', md: '2xs' }}>
      {title && (
        <Grid.Inner gridRowGap={0}>
          <Divider gridColumn="span 12" />
          <Text gridColumn="span 12" as="h2" size="overline" my="2xs">
            {title}
          </Text>
        </Grid.Inner>
      )}
      <Grid.Inner
        as={UnorderedList}
        gridRowGap="lg"
        gridColumnGap={{ base: '3xs', md: 'sm' }}
        mx={0}
      >
        {visiblePeople.map((person, i) => (
          <Person
            key={i}
            drawerHeader={drawerHeader}
            name={person.name}
            role={person.role}
            image={person.image}
            biography={person.biography}
          />
        ))}
      </Grid.Inner>
      {hasHiddenPeople && (
        <Grid.Inner>
          <Box display="flex" justifyContent="center" gridColumn="span 12">
            <Button
              variant="outline"
              isWide
              width={{ base: '100%', sm: 'auto' }}
              onClick={handleShowMore}
            >
              Show more
            </Button>
          </Box>
        </Grid.Inner>
      )}
    </Grid>
  );
}
